/**
 * Hero Banner Slider
 * Vanilla JS, no dependencies.
 * Supports: touch swipe, mouse drag, autoplay, arrows, dots, keyboard nav.
 */
(function () {
  'use strict';

  class HeroBanner {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-hero-viewport]');
      this.track = root.querySelector('[data-hero-track]');
      this.slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
      this.prevBtn = root.querySelector('[data-hero-prev]');
      this.nextBtn = root.querySelector('[data-hero-next]');
      this.dots = Array.from(root.querySelectorAll('[data-hero-dot]'));

      this.slideCount = this.slides.length;
      if (this.slideCount === 0) return;

      this.currentIndex = 0;
      this.autoplay = root.dataset.autoplay === 'true';
      this.autoplayInterval = (parseFloat(root.dataset.autoplayInterval) || 5) * 1000;
      this.transitionSpeed = parseFloat(root.dataset.transitionSpeed) || 0.5;

      this.isDragging = false;
      this.dragStartX = 0;
      this.dragDeltaX = 0;
      this.viewportWidth = this.viewport.offsetWidth;

      this.autoplayTimer = null;

      this.root.style.setProperty('--hero-transition-speed', this.transitionSpeed + 's');

      this.bindEvents();
      this.goToSlide(0, false);

      if (this.autoplay && this.slideCount > 1) {
        this.startAutoplay();
      }
    }

    bindEvents() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.pauseAutoplay();
          this.goToPrev();
          this.resumeAutoplay();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.pauseAutoplay();
          this.goToNext();
          this.resumeAutoplay();
        });
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10);
          this.pauseAutoplay();
          this.goToSlide(index);
          this.resumeAutoplay();
        });
      });

      // Keyboard navigation
      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.pauseAutoplay();
          this.goToPrev();
          this.resumeAutoplay();
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.pauseAutoplay();
          this.goToNext();
          this.resumeAutoplay();
        }
      });

      // Pause autoplay on hover/focus
      this.root.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.root.addEventListener('mouseleave', () => this.resumeAutoplay());
      this.root.addEventListener('focusin', () => this.pauseAutoplay());
      this.root.addEventListener('focusout', () => this.resumeAutoplay());

      // Touch events
      this.viewport.addEventListener('touchstart', (e) => this.handleDragStart(e), { passive: true });
      this.viewport.addEventListener('touchmove', (e) => this.handleDragMove(e), { passive: true });
      this.viewport.addEventListener('touchend', () => this.handleDragEnd());

      // Mouse drag events
      this.viewport.addEventListener('mousedown', (e) => this.handleDragStart(e));
      window.addEventListener('mousemove', (e) => this.handleDragMove(e));
      window.addEventListener('mouseup', () => this.handleDragEnd());

      // Prevent native image drag ghost
      this.viewport.addEventListener('dragstart', (e) => e.preventDefault());

      // Resize
      window.addEventListener('resize', () => {
        this.viewportWidth = this.viewport.offsetWidth;
        this.setTrackPosition(false);
      });
    }

    handleDragStart(e) {
      if (this.slideCount <= 1) return;
      this.isDragging = true;
      this.dragStartX = this.getEventX(e);
      this.dragDeltaX = 0;
      this.root.setAttribute('data-dragging', 'true');
      this.pauseAutoplay();
    }

    handleDragMove(e) {
      if (!this.isDragging) return;
      const currentX = this.getEventX(e);
      this.dragDeltaX = currentX - this.dragStartX;
      const basePercent = -this.currentIndex * 100;
      const dragPercent = (this.dragDeltaX / this.viewportWidth) * 100;
      this.track.style.transform = `translateX(${basePercent + dragPercent}%)`;
    }

    handleDragEnd() {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.root.removeAttribute('data-dragging');

      const threshold = this.viewportWidth * 0.15;

      if (this.dragDeltaX > threshold) {
        this.goToPrev();
      } else if (this.dragDeltaX < -threshold) {
        this.goToNext();
      } else {
        this.setTrackPosition(true);
      }

      this.dragDeltaX = 0;
      this.resumeAutoplay();
    }

    getEventX(e) {
      if (e.touches && e.touches.length) {
        return e.touches[0].clientX;
      }
      if (e.changedTouches && e.changedTouches.length) {
        return e.changedTouches[0].clientX;
      }
      return e.clientX;
    }

    goToPrev() {
      const newIndex = (this.currentIndex - 1 + this.slideCount) % this.slideCount;
      this.goToSlide(newIndex);
    }

    goToNext() {
      const newIndex = (this.currentIndex + 1) % this.slideCount;
      this.goToSlide(newIndex);
    }

    goToSlide(index, animate = true) {
      this.currentIndex = index;
      this.setTrackPosition(animate);
      this.updateActiveStates();
    }

    setTrackPosition(animate) {
      if (!animate) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = '';
      }
      const percent = -this.currentIndex * 100;
      this.track.style.transform = `translateX(${percent}%)`;

      if (!animate) {
        // Force reflow so the transition-less transform is applied immediately
        void this.track.offsetHeight;
        this.track.style.transition = '';
      }
    }

    updateActiveStates() {
      this.slides.forEach((slide, i) => {
        const isActive = i === this.currentIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        // Lazy-load images as they become active
        if (isActive) {
          const img = slide.querySelector('img[loading="lazy"]');
          if (img && img.dataset.loaded !== 'true') {
            img.loading = 'eager';
            img.dataset.loaded = 'true';
          }
        }
      });

      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    startAutoplay() {
      if (this.slideCount <= 1) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        this.goToNext();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    pauseAutoplay() {
      this.stopAutoplay();
    }

    resumeAutoplay() {
      if (this.autoplay && this.slideCount > 1) {
        this.startAutoplay();
      }
    }

    destroy() {
      this.stopAutoplay();
    }
  }

  function initHeroBanners(container) {
    const scope = container || document;
    const banners = scope.querySelectorAll('[data-hero-banner]');
    banners.forEach((banner) => {
      if (banner.heroBannerInstance) return;
      banner.heroBannerInstance = new HeroBanner(banner);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initHeroBanners());

  // Shopify theme editor support: re-init on section load/select
  document.addEventListener('shopify:section:load', (e) => initHeroBanners(e.target));

  document.addEventListener('shopify:section:unload', (e) => {
    const banner = e.target.querySelector('[data-hero-banner]');
    if (banner && banner.heroBannerInstance) {
      banner.heroBannerInstance.destroy();
      delete banner.heroBannerInstance;
    }
  });
})();
